import equal from "fast-deep-equal";
import { Period } from "hooks/periodIndex";
import {
  ChildMixUpdate,
  MaterialQuantityUnit,
  MixBasket,
  MixBasketLayer,
  MixMaterial,
  MixQuantity,
  PlannedResultsRead,
  ProductionMixResultRead,
  SteelGrade,
  SummarisedProductionMixResultRead,
  UnsummarisedProductionMixResult,
} from "src/store/api/generatedApi";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import { z } from "zod/v4";
import { PlanId } from "../boundary/PlanId";
import {
  AdditionRow,
  BasketRow,
  EmptyBasketRow,
  EmptyProfileRow,
  ProfileRow,
  RatioRow,
  Row,
  TotalRow,
} from "./types";

export const TableBasketNumberSchema = z.number().int().min(2);
export const TableProfileBasketNumberSchema = z.literal(1);

type TableBasketNumber = z.infer<typeof TableBasketNumberSchema>;

// Layer order is meaningful (each MixBasketLayer represents a physical layer
// in the basket, and a basket may legitimately carry multiple layers of the
// same material). Equality therefore compares layers in their layer_number
// order with the full content (material_name + quantity + layer_number)
// included. Cell-edit helpers scale every matching layer in proportion to
// the new total and preserve each layer_number so the multi-layer shape
// survives a cell edit (and a no-op cell touch leaves the row untouched);
// the row-state layer_numbers are the authoritative identity field and are
// only re-numbered contiguously from 1 at serialisation time, where the
// backend's pandera schema requires it.

const nonZeroLayers = (layers: MixBasketLayer[]): MixBasketLayer[] =>
  layers.filter((layer) => layer.quantity.value !== 0);

const sortLayersByNumber = (layers: MixBasketLayer[]): MixBasketLayer[] =>
  [...layers].sort((a, b) => a.layer_number - b.layer_number);

const layersEqual = (a: MixBasketLayer[], b: MixBasketLayer[]): boolean =>
  equal(
    sortLayersByNumber(nonZeroLayers(a)),
    sortLayersByNumber(nonZeroLayers(b))
  );

export const basketsEqual = (a: MixBasket[], b: MixBasket[]): boolean => {
  const nonEmptyA = a.filter(
    (basket) => nonZeroLayers(basket.layers).length > 0
  );
  const nonEmptyB = b.filter(
    (basket) => nonZeroLayers(basket.layers).length > 0
  );
  if (nonEmptyA.length !== nonEmptyB.length) return false;
  const byNumberA = typeSafeObjectFromEntries(
    nonEmptyA.map((basket) => [basket.basket_number, basket])
  );
  const byNumberB = typeSafeObjectFromEntries(
    nonEmptyB.map((basket) => [basket.basket_number, basket])
  );
  const numbersA = Object.keys(byNumberA).sort();
  const numbersB = Object.keys(byNumberB).sort();
  if (!equal(numbersA, numbersB)) return false;
  return numbersA.every((key) => {
    const basketA = byNumberA[Number(key)];
    const basketB = byNumberB[Number(key)];
    return (
      basketA !== undefined &&
      basketB !== undefined &&
      layersEqual(basketA.layers, basketB.layers)
    );
  });
};

export const isMixEqual = (
  a: ProductionMixResultRead,
  b: ProductionMixResultRead
): boolean =>
  a.mix.id === b.mix.id &&
  a.name === b.name &&
  (a.description ?? "") === (b.description ?? "") &&
  equal(a.production, b.production) &&
  basketsEqual(a.mix.baskets, b.mix.baskets);

export const areMixesEqual = (
  resultsA: ProductionMixResultRead[],
  resultsB: ProductionMixResultRead[]
): boolean => {
  const byIdA = typeSafeObjectFromEntries(
    resultsA.map((result) => [result.mix.id, result])
  );
  const byIdB = typeSafeObjectFromEntries(
    resultsB.map((result) => [result.mix.id, result])
  );
  if (!equal(Object.keys(byIdA).sort(), Object.keys(byIdB).sort())) {
    return false;
  }
  return typeSafeObjectKeys(byIdA).every((id) => {
    const resultA = byIdA[id];
    const resultB = byIdB[id];
    return (
      resultA !== undefined &&
      resultB !== undefined &&
      isMixEqual(resultA, resultB)
    );
  });
};

// A freshly loaded plan carries the layout the backend persisted. Re-summarising
// it with per_basket_totals re-stratifies every mix via the tenant's algorithmic
// layerer, which is not a fixed point on a manually-laid-out (or FULL-mode) mix.
// A mix whose stored layout diverges from that auto-stratification must have been
// saved manually, so it should round-trip as manual on later summarises. This is
// an inferred property of stored data, not a user edit — callers must keep it out
// of any unsaved-edits gate.
export const detectManuallyLayeredMixes = (
  storedResults: ProductionMixResultRead[],
  summarisedMixes: SummarisedProductionMixResultRead[],
  knownManualMixIds: ReadonlySet<number>
): Set<number> => {
  const summarisedById = new Map(
    summarisedMixes.map(
      (summarisedMix) => [summarisedMix.original_mix_id, summarisedMix] as const
    )
  );
  const detected = new Set<number>();
  for (const result of storedResults) {
    if (knownManualMixIds.has(result.mix.id)) continue;
    const summarised = summarisedById.get(result.mix.id);
    if (summarised === undefined) continue;
    if (!basketsEqual(summarised.mix.baskets, result.mix.baskets)) {
      detected.add(result.mix.id);
    }
  }
  return detected;
};

const filterSteelGrades = (
  result: ProductionMixResultRead,
  steelGradeByName: Record<string, SteelGrade>
): SteelGrade[] =>
  result.production
    .map((item) => steelGradeByName[item.steel_grade_name])
    .filter((steelGrade): steelGrade is SteelGrade => steelGrade !== undefined);

const computeNumberOfHeats = (result: ProductionMixResultRead): number =>
  result.production.reduce((sum, item) => sum + item.num_heats, 0);

const findBasket = (
  result: ProductionMixResultRead,
  basketNumber: number
): MixBasket | undefined =>
  result.mix.baskets.find((basket) => basket.basket_number === basketNumber);

const makeBasketRow =
  (basketNumber: TableBasketNumber) =>
  (
    period: Period,
    result: ProductionMixResultRead,
    steelGrades: SteelGrade[]
  ): Omit<EmptyBasketRow, "id"> | Omit<BasketRow, "id"> => {
    const basket = findBasket(result, basketNumber);
    if (basket && basket.layers.length > 0) {
      return {
        type: "basket",
        steelGrades,
        layers: basket.layers,
        mixId: result.mix.id,
        mixName: result.name,
        basketNumber,
        period,
      };
    } else {
      return {
        type: "empty_basket",
        mixId: result.mix.id,
        mixName: result.name,
        steelGrades,
        basketNumber,
        period,
      };
    }
  };

const DEFAULT_BASKET_NUMBERS = [1, 2, 3];

export const originalMixPrice = (mix: ProductionMixResultRead): number | null =>
  mix.mix.summary?.cost.display_cost_per_tonne ?? null;

const makeAdditionRow = (
  period: Period,
  result: ProductionMixResultRead,
  steelGrades: SteelGrade[]
): Omit<AdditionRow, "id"> => {
  const basket1 = findBasket(result, 1);
  const grabLayers = (basket1?.layers ?? []).filter(
    (layer) => layer.quantity.unit === "grab"
  );
  return {
    type: "addition",
    steelGrades,
    layers: grabLayers,
    mixId: result.mix.id,
    mixName: result.name,
    basketNumber: 2,
    period,
  };
};

const makeRatioRow = (
  planId: PlanId | null,
  period: Period,
  result: ProductionMixResultRead,
  steelGrades: SteelGrade[]
): Omit<RatioRow, "id"> => {
  const basket1 = findBasket(result, 1);
  const ratioLayers = (basket1?.layers ?? []).filter(
    (layer) => layer.quantity.unit !== "grab"
  );
  return {
    type: "ratio",
    planId,
    originalPrice: originalMixPrice(result),
    name: result.name,
    steelGrades,
    layers: ratioLayers,
    mixId: result.mix.id,
    mixName: result.name,
    numberOfHeats: computeNumberOfHeats(result),
    period,
    steelGradeProduction: result.production,
  };
};

const makeProfileRow = (
  planId: PlanId | null,
  period: Period,
  result: ProductionMixResultRead,
  steelGrades: SteelGrade[]
): Omit<ProfileRow, "id"> | Omit<EmptyProfileRow, "id"> => {
  const basket1 = findBasket(result, 1);
  const numberOfHeats = computeNumberOfHeats(result);
  if (basket1 && basket1.layers.length > 0) {
    return {
      type: "profile",
      planId,
      originalPrice: originalMixPrice(result),
      name: result.name,
      steelGrades,
      baskets: result.mix.baskets,
      layers: basket1.layers,
      mixId: result.mix.id,
      mixName: result.name,
      numberOfHeats,
      period,
      steelGradeProduction: result.production,
      description: result.description ?? undefined,
    };
  } else {
    return {
      type: "empty_profile",
      planId,
      name: result.name,
      steelGrades,
      mixId: result.mix.id,
      mixName: result.name,
      basketNumber: 1,
      baskets: result.mix.baskets,
      originalPrice: originalMixPrice(result),
      numberOfHeats,
      period,
      steelGradeProduction: result.production,
      description: result.description ?? undefined,
    };
  }
};

const makeTotalRow = (
  period: Period,
  result: ProductionMixResultRead,
  steelGrades: SteelGrade[]
): Omit<TotalRow, "id"> => ({
  type: "total",
  steelGrades,
  mixId: result.mix.id,
  mixName: result.name,
  period,
});

export const isMixHeaderRow = (row: Row): boolean => {
  switch (row.type) {
    case "profile":
    case "empty_profile":
    case "ratio":
      return true;
    case "basket":
    case "empty_basket":
    case "addition":
    case "total":
      return false;
  }
};

export const makeRows = (
  planId: PlanId | null,
  period: Period,
  results: ProductionMixResultRead[],
  steelGrades: SteelGrade[],
  mixQuantity: MixQuantity,
  basketNumbers: number[] = DEFAULT_BASKET_NUMBERS
): Row[] => {
  const steelGradeByName = typeSafeObjectFromEntries(
    steelGrades.map((steelGrade) => [steelGrade.name, steelGrade])
  );
  return results
    .flatMap((result): Omit<Row, "id">[] => {
      const filteredSteelGrades = filterSteelGrades(result, steelGradeByName);
      return mixQuantity === "grabs"
        ? [
            makeRatioRow(planId, period, result, filteredSteelGrades),
            makeAdditionRow(period, result, filteredSteelGrades),
          ]
        : [
            makeProfileRow(planId, period, result, filteredSteelGrades),
            ...basketNumbers
              .filter((number) => number > 1)
              .map((number) =>
                makeBasketRow(number)(period, result, filteredSteelGrades)
              ),
            makeTotalRow(period, result, filteredSteelGrades),
          ];
    })
    .map((row, index) => ({ ...row, id: index })) as Row[];
};

export const applyMixUpdateToRows = (
  rows: Row[],
  mixId: number,
  mixUpdate: ChildMixUpdate
): Row[] => {
  const inlineMix = mixUpdate.new_mix_production.mix;
  if (!inlineMix) return rows;
  const updatedBaskets = inlineMix.baskets;
  const basket1Layers =
    updatedBaskets.find((basket) => basket.basket_number === 1)?.layers ?? [];
  const grabBasket1Layers = basket1Layers.filter(
    (layer) => layer.quantity.unit === "grab"
  );
  const massBasket1Layers = basket1Layers.filter(
    (layer) => layer.quantity.unit !== "grab"
  );

  const nextRows = rows.map((row) => {
    if (row.mixId !== mixId) return row;
    switch (row.type) {
      case "profile":
        return equal(row.layers, basket1Layers)
          ? row
          : { ...row, layers: basket1Layers };
      case "ratio":
        return equal(row.layers, massBasket1Layers)
          ? row
          : { ...row, layers: massBasket1Layers };
      case "addition":
        return equal(row.layers, grabBasket1Layers)
          ? row
          : { ...row, layers: grabBasket1Layers };
      case "basket": {
        const basketLayers =
          updatedBaskets.find(
            (basket) => basket.basket_number === row.basketNumber
          )?.layers ?? [];
        return equal(row.layers, basketLayers)
          ? row
          : { ...row, layers: basketLayers };
      }
      default:
        return row;
    }
  });
  return nextRows.every((row, index) => row === rows[index]) ? rows : nextRows;
};

export const mergeRowsAndMix = (
  rows: Row[],
  result: ProductionMixResultRead
): ProductionMixResultRead => {
  const matchingRows = rows.filter((row) => row.mixId === result.mix.id);
  if (matchingRows.length === 0) return result;
  const profileRow = matchingRows.find(
    (row): row is ProfileRow | EmptyProfileRow | RatioRow =>
      row.type === "profile" ||
      row.type === "empty_profile" ||
      row.type === "ratio"
  );
  if (!profileRow) {
    throw new Error("Profile row missing on edit in Plan Table");
  }

  const additionRows = matchingRows.filter(
    (row): row is AdditionRow => row.type === "addition"
  );
  const basketRows = matchingRows.filter(
    (row): row is BasketRow => row.type === "basket"
  );

  const profileLayers =
    profileRow.type === "profile" || profileRow.type === "ratio"
      ? profileRow.layers
      : [];
  const additionLayers = additionRows.flatMap((row) => row.layers);
  const basket1Layers = [...profileLayers, ...additionLayers];

  const otherBaskets: MixBasket[] = basketRows.map((row) => ({
    basket_number: row.basketNumber,
    layers: row.layers,
  }));

  const isDriOnly =
    profileRow.type === "empty_profile" && otherBaskets.length === 0;

  const baskets: MixBasket[] = isDriOnly
    ? []
    : [{ basket_number: 1, layers: basket1Layers }, ...otherBaskets];

  return {
    ...result,
    description:
      profileRow.type === "profile" || profileRow.type === "empty_profile"
        ? (profileRow.description ?? null)
        : result.description,
    mix: {
      ...result.mix,
      baskets,
    },
  };
};

export const makePostDataGridProcessingCallback = <T, R>(
  callback: (arg: T) => R
): ((arg: T) => Promise<R>) => {
  return async (arg: T) => {
    await new Promise((resolve) => setTimeout(resolve, 100));
    return callback(arg);
  };
};

export const updateRowEventParam = "__updateRow__";
export const resetEventParam = "__reset__";
export const disableEditEventParam = "__disableEdit__";
export const enableEditEventParam = "__enableEdit__";

export const getDriMaterial = (mix: {
  furnace?: MixMaterial[];
}): MixMaterial | undefined => mix.furnace?.[0];

export const getDriMass = (mix: { furnace?: MixMaterial[] }): number =>
  getDriMaterial(mix)?.quantity.value ?? 0;

// A basket may contain multiple layers of the same material (representing
// physical stacking). The editable plan table aggregates by material — every
// helper that maps "material X in basket N" to a single value sums layers,
// and every cell-level write replaces the full set of matching layers with a
// single consolidated entry at the new value.

export const getMaterialMass = (
  layers: MixBasketLayer[],
  materialName: string
): number =>
  layers
    .filter((layer) => layer.material_name === materialName)
    .reduce((sum, layer) => sum + layer.quantity.value, 0);

export interface AggregatedBasketMaterial {
  materialName: string;
  unit: MaterialQuantityUnit;
  totalMass: number;
}

// Read-only displays (CompactMixDisplay, MixDisplayTable) collapse a basket's
// layers into one row per material — same convention as the editable table —
// so the same material with stacked layers shows once with the total mass.
// Optionally orders the result by a material → display-ordering map.
export const aggregateBasketLayersByMaterial = (
  layers: MixBasketLayer[],
  orderingByName?: Record<string, number>
): AggregatedBasketMaterial[] => {
  const byName = new Map<string, AggregatedBasketMaterial>();
  for (const layer of layers) {
    const existing = byName.get(layer.material_name);
    if (existing) {
      existing.totalMass += layer.quantity.value;
    } else {
      byName.set(layer.material_name, {
        materialName: layer.material_name,
        unit: layer.quantity.unit,
        totalMass: layer.quantity.value,
      });
    }
  }
  const aggregated = [...byName.values()];
  if (orderingByName) {
    aggregated.sort(
      (a, b) =>
        (orderingByName[a.materialName] ?? 0) -
        (orderingByName[b.materialName] ?? 0)
    );
  }
  return aggregated;
};

// Round each value to a multiple of `roundingUnits` so the rounded total
// equals `round(sum(values) / roundingUnits) * roundingUnits`. Floor each
// to the rounding grid, then assign one extra unit to the indices with the
// largest fractional remainders until the rounded sum reaches the target.
// Ties on fractional part fall back to the larger original index, so a
// caller passing layers sorted by layer_number ascending sees the residual
// land at the top of the stack.
const distributeRoundedTotal = (
  values: number[],
  roundingUnits: number
): number[] => {
  if (values.length === 0) return [];
  const increments = values.map((value) => value / roundingUnits);
  const floored = increments.map((value) => Math.floor(value));
  const remainders = increments.map(
    (value, index) => value - (floored[index] ?? 0)
  );
  const targetIncrements = Math.round(
    increments.reduce((sum, value) => sum + value, 0)
  );
  const extras = targetIncrements - floored.reduce((sum, x) => sum + x, 0);
  const ranked = values
    .map((_, index) => index)
    .sort((a, b) => (remainders[b] ?? 0) - (remainders[a] ?? 0) || b - a);
  const extrasMask = values.map(() => 0);
  for (let i = 0; i < extras; i++) {
    const target = ranked[i];
    if (target !== undefined) extrasMask[target] = 1;
  }
  return values.map(
    (_, index) =>
      ((floored[index] ?? 0) + (extrasMask[index] ?? 0)) * roundingUnits
  );
};

// Cell edits are aggregate-by-material, but the underlying basket may stack
// multiple layers of the same material; scale each matching layer in
// proportion to the new total so the existing stacking is preserved, then
// round each to `roundingUnits` so the displayed values match what the
// backend would persist. Original array positions are kept so the modal
// rendering and the saved canonical order — sorted by layer_number — agree
// once the serialiser renumbers contiguously from 1. A new total of zero
// removes every matching layer (the typing-0-to-clear shortcut), keeping
// the wire payload valid for the MANUAL save path which rejects ≤ 0
// layers. Brand-new materials slot in just after the basket's current max
// layer_number; an all-zero matching set is degenerate (no ratio) and
// collapses to a single entry at min(matching layer_number) in its
// existing array slot.
export const scaleMaterialLayersToTotal = (
  layers: MixBasketLayer[],
  materialName: string,
  newTotal: number,
  fallbackUnit: MaterialQuantityUnit,
  roundingUnits: number
): MixBasketLayer[] => {
  const roundedTotal = Math.round(newTotal / roundingUnits) * roundingUnits;

  if (roundedTotal <= 0) {
    return layers.filter((layer) => layer.material_name !== materialName);
  }

  const matching = layers.filter(
    (layer) => layer.material_name === materialName
  );

  if (matching.length === 0) {
    const nextLayerNumber =
      layers.length === 0
        ? 1
        : layers.reduce((max, layer) => Math.max(max, layer.layer_number), 0) +
          1;
    return [
      ...layers,
      {
        material_name: materialName,
        quantity: { value: roundedTotal, unit: fallbackUnit },
        layer_number: nextLayerNumber,
      },
    ];
  }

  const sortedMatching = [...matching].sort(
    (a, b) => a.layer_number - b.layer_number
  );
  const currentTotal = matching.reduce(
    (sum, layer) => sum + layer.quantity.value,
    0
  );

  if (currentTotal === 0) {
    const firstMatching = sortedMatching[0];
    if (firstMatching === undefined) return layers;
    const minLayerNumber = firstMatching.layer_number;
    const baseUnit = firstMatching.quantity.unit;
    let kept = false;
    return layers.flatMap((layer) => {
      if (layer.material_name !== materialName) return [layer];
      if (layer.layer_number === minLayerNumber && !kept) {
        kept = true;
        return [
          {
            ...layer,
            quantity: { value: roundedTotal, unit: baseUnit },
            layer_number: minLayerNumber,
          },
        ];
      }
      return [];
    });
  }

  const ratio = newTotal / currentTotal;
  const scaledValues = sortedMatching.map(
    (layer) => layer.quantity.value * ratio
  );
  const roundedValues = distributeRoundedTotal(scaledValues, roundingUnits);
  const valueByLayerNumber = new Map(
    sortedMatching.map((layer, index): [number, number] => [
      layer.layer_number,
      roundedValues[index] ?? 0,
    ])
  );
  return layers.map((layer) => {
    if (layer.material_name !== materialName) return layer;
    const newValue = valueByLayerNumber.get(layer.layer_number);
    if (newValue === undefined) return layer;
    return {
      ...layer,
      quantity: { ...layer.quantity, value: newValue },
    };
  });
};

export const removeMaterialLayers = (
  layers: MixBasketLayer[],
  materialName: string
): MixBasketLayer[] =>
  layers.filter((layer) => layer.material_name !== materialName);

// Backend pandera schema enforces basket_layer_number >= 1 and unique
// within a basket. Row-state edits can leave gaps (e.g., after a removal)
// or a slot just past the current max (after a brand-new material is
// added); this helper sorts by the row-state layer_number — the
// authoritative stacking order — and renumbers contiguously from 1 before
// serialisation. Sorting first matters because the array order can
// disagree with the layer_number order for multi-layer same-material
// baskets.
export const renumberBasketLayers = (baskets: MixBasket[]): MixBasket[] =>
  baskets.map((basket) => ({
    ...basket,
    layers: sortLayersByNumber(basket.layers).map((layer, index) => ({
      ...layer,
      layer_number: index + 1,
    })),
  }));

// The backend's manual save path rejects zero-value layers and layer_numbers
// that are not contiguous from 1 within a basket.
export const normaliseManualBaskets = (baskets: MixBasket[]): MixBasket[] =>
  renumberBasketLayers(
    baskets.map((basket) => ({
      ...basket,
      layers: basket.layers.filter((layer) => layer.quantity.value > 0),
    }))
  );

// getPlanResults / getDeployedPlanResults are cached per (planId, period), but
// the response shape carries every period the plan exposes. When upserting a
// write response into the cache for a specific period, strip the response's
// `periods` dict to just that period — otherwise a multi-period save smears
// the same payload across each period's cache entry, masking what the GET
// endpoint would return.
interface ResultsWithPeriods {
  periods: PlannedResultsRead["periods"];
}

export const filterPlanResultsToPeriod = <T extends ResultsWithPeriods>(
  results: T,
  period: Period | number
): T => {
  const periodKey = period.toString();
  const matchingPeriod = results.periods[periodKey];
  return {
    ...results,
    periods: matchingPeriod ? { [periodKey]: matchingPeriod } : {},
  };
};

export const tableRowsToBackendMixes = (
  rows: Row[]
): UnsummarisedProductionMixResult[] => {
  const profileLikeRows = rows.filter(
    (row): row is ProfileRow | EmptyProfileRow | RatioRow =>
      row.type === "profile" ||
      row.type === "empty_profile" ||
      row.type === "ratio"
  );
  return profileLikeRows.map((profileRow) => {
    const basketRows = rows.filter(
      (row): row is BasketRow =>
        row.type === "basket" && row.mixId === profileRow.mixId
    );
    const additionRows = rows.filter(
      (row): row is AdditionRow =>
        row.type === "addition" && row.mixId === profileRow.mixId
    );

    const profileLayers =
      profileRow.type === "profile" || profileRow.type === "ratio"
        ? profileRow.layers
        : [];
    const additionLayers = additionRows.flatMap((row) => row.layers);
    const basket1Layers = [...profileLayers, ...additionLayers];

    const baskets: MixBasket[] = [
      ...(basket1Layers.length > 0
        ? [{ basket_number: 1, layers: basket1Layers }]
        : []),
      ...basketRows.map((row) => ({
        basket_number: row.basketNumber,
        layers: row.layers,
      })),
    ];

    return {
      original_mix_id: profileRow.mixId,
      name: profileRow.mixName,
      description:
        profileRow.type === "profile" || profileRow.type === "empty_profile"
          ? (profileRow.description ?? null)
          : null,
      production: profileRow.steelGradeProduction,
      mix: { baskets: renumberBasketLayers(baskets) },
    };
  });
};

export const totalBasketMass = (layers: MixBasketLayer[]): number =>
  layers.reduce((sum, layer) => sum + layer.quantity.value, 0);
